import { ArrowSmRightIcon, RefreshIcon } from '@heroicons/react/solid'
import { useNavigate } from 'react-router-dom'
import { savedTokens } from 'src/auth'
import {
  Anchor,
  ErrorMissingData,
  ErrorNotLoggedIn,
  Form,
  LocationInfo,
  Paragraph,
  StickyFooter,
  Title,
} from 'src/components'
import { useEditOrganization } from '../../Onboarding/queries'
import { useResyncSubscriptionProvisioning } from '../queries'
import {
  savedAccountId,
  savedArn,
  savedConnectUrl,
  savedInstanceId,
  savedLoginUrl,
  savedPrincipalArn,
  savedRegion,
  savedRoleArn,
} from '../storage'
import EmailHelpBox from 'src/components/Layout/EmailHelpBox'
import { getBackendByRegion } from 'src/regions'
import InputField from 'src/components/Shared/UIComponents/InputField'
import CustomButton from 'src/components/Shared/UIComponents/Button'
import { toast } from 'sonner'

type Props = {
  organization: string
  username: string
  connectUrl: string
  region: string
  instanceId: string
  accountId: string
  arn: string
}

function Sso(props: Props) {
  const navigate = useNavigate()
  const editOrganization = useEditOrganization()
  const resyncSubscription = useResyncSubscriptionProvisioning()
  const [loginUrl, setLoginUrl] = savedLoginUrl.useState('')
  const [principalArn, setPrincipalArn] = savedPrincipalArn.useState('')
  const [roleArn, setRoleArn] = savedRoleArn.useState('')

  const link = new URL(
    `/token/sso?org=${props.organization}`,
    getBackendByRegion(savedTokens.get()?.region),
  ).href

  return (
    <>
      <Form
        onSubmit={async (e) => {
          e.preventDefault()
          if (
            loginUrl &&
            principalArn &&
            props.region &&
            roleArn &&
            props.connectUrl &&
            props.instanceId &&
            props.accountId &&
            props.arn
          ) {
            await editOrganization.mutateAsync({
              cognito_url: '',
              userPoolAppClientId: '',
              userPoolClientSecret: '',
              userPoolId: '',
              userPoolRegion: '',
              userPoolTokenType: '',
              aws_connect_saml_url: props.connectUrl,
              iam_organization_admin_accessKey: '',
              iam_organization_admin_region: '', //didn't exist
              iam_organization_usergroup: '', //didn't exist
              redirect_uri: '', //didn't exist
              aws_connect_type: 'SALESFORCE',
              iam_organization_admin_accessSecret: '',
              iam_organization_policy_arn: '',
              organization_connect_arn: props.arn,
              organization_connect_instance_id: props.instanceId,
              organization_connect_account_id: props.accountId,
              code: props.organization,
              setupStep: 'salesforce-configuration',
              username: props.username,
              organization: props.organization,
              region: props.region,
              sso: {
                loginUrl: loginUrl,
                principalArn: principalArn,
                region: props.region,
                roleArn: roleArn,
                type: 'salesforce',
              },
              application: {
                chatregion: props.region,
                sf_url: 'https://login.salesforce.com/services/oauth2/token?',
                sf_endpoint: new URL(
                  '/services/apexrest/api/v1/service-cloud-voice/voiceCalls',
                  loginUrl,
                ).href,
              },
            })

            // TODO: check if this is needed in this step or later in the ../config
            await resyncSubscription.mutateAsync({
              organization: props.organization,
            })

            navigate('../config')
          } else {
            toast.error('ARN is not valid.')
          }
        }}
      >
        <Title label="SSO ACS URL" />

        <LocationInfo path="Salesforce/Setup/Apps/App Manager/Edit" />

        <div className="col-span-full">
          <Paragraph>
            To set up Salesforce as an identity provider for Mobile Connect,
            please follow the steps at{' '}
            <Anchor href="https://amazon-connect.github.io/amazon-connect-salesforce-cti/docs/classic/appendices/appendix-b-configuring-salesforce-as-your-identity-provider/01-configuring-salesforce-as-your-identity-provider/">
              Configuring Salesforce as Your Identity Provider
            </Anchor>
            . The only difference is that you need to use the below URL as ACS
            URL instead of{' '}
            <span className="line-through">
              https://signin.aws.amazon.com/saml
            </span>
            .
          </Paragraph>
          <div className="flex items-end mt-3 mb-14 gap-4">
            <InputField
              label="ACS URL"
              placeholder=""
              value={link}
              disabled
              setValue={() => {}}
              className="flex-1 bg-foreground"
            />
            <CustomButton
              type="button"
              label="copy"
              onClick={() => {
                navigator.clipboard.writeText(link)
                toast.success(`Setup link has been copied to your clipboard.`)
              }}
            />
          </div>
        </div>

        <Title label="SSO Configuration" />

        <LocationInfo path="Salesforce/Setup/Apps/App Manager/Manage" />

        <Paragraph className="col-span-full">
          After following the instructions, please copy and paste the values
          from your Salesforce Connected App into the form below.
        </Paragraph>

        <img
          className="border-2 col-span-full"
          src={require('src/routes/Salesforce/images/login-url.png')}
          width="920"
          alt="login url"
        />

        <div className="col-span-full">
          <InputField
            label="IdP-Initiated Login Url"
            placeholder="https://INSTANCE.my.salesforce.com/idp/login?app=ID"
            value={loginUrl}
            setValue={(value) => setLoginUrl(value.trim())}
            type="url"
          />
        </div>

        <img
          className="border-2 col-span-full"
          src={require('src/routes/Salesforce/images/roles.png')}
          width="920"
          alt="login url"
        />

        <div className="flex flex-col col-span-full gap-5">
          <InputField
            label="Identity provider ARN"
            value={principalArn}
            setValue={(value) => {
              setPrincipalArn(value.trim())
            }}
            title="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
            placeholder="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
            pattern="arn:aws:iam::\d{12}:saml-provider\/[A-Za-z0-9\-_]+"
            tooltip={
              <p>
                Navigate to{' '}
                <b>
                  AWS Management console -&gt; IAM -&gt; Identity Providers
                  -&gt; AzureAD_Connect_Admin -&gt; ARN
                </b>
              </p>
            }
          />

          <InputField
            label="Role ARN"
            placeholder="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
            pattern="arn:aws:iam::\d{12}:role\/[A-Za-z0-9\-_]+"
            value={roleArn}
            setValue={(value) => {
              setRoleArn(value.trim())
            }}
            title="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
            tooltip={
              <p>
                Navigate to{' '}
                <b>
                  AWS Management console -&gt; IAM -&gt; Roles -&gt; Click Azure
                  role -&gt; ARN
                </b>
              </p>
            }
          />
        </div>

        <StickyFooter>
          <EmailHelpBox className="flex-1" />
          <CustomButton
            type="submit"
            label="next"
            isLoading={
              editOrganization.isLoading || resyncSubscription.isLoading
            }
            icon={<ArrowSmRightIcon />}
            iconPosition="right"
          />
        </StickyFooter>
      </Form>
    </>
  )
}

export default function ValidateSso() {
  const navigate = useNavigate()
  const [tokens] = savedTokens.useState(null)
  const connectUrl = savedConnectUrl.get()
  const instanceId = savedInstanceId.get()
  const accountId = savedAccountId.get()
  const region = savedRegion.get()
  const arn = savedArn.get()

  if (!tokens) {
    return <ErrorNotLoggedIn />
  }

  if (!connectUrl || !region || !instanceId || !accountId || !arn) {
    return (
      <ErrorMissingData
        missingValues={{
          connectUrl: !connectUrl,
          instanceId: !instanceId,
          accountId: !accountId,
          arn: !arn,
        }}
      >
        <CustomButton
          label="restart setup"
          onClick={() => navigate('../arn')}
          className="ml-8"
          icon={<RefreshIcon />}
          iconPosition="left"
        />
      </ErrorMissingData>
    )
  }

  return (
    <Sso
      organization={tokens.organization}
      username={tokens.username}
      connectUrl={connectUrl}
      region={region}
      instanceId={instanceId}
      accountId={accountId}
      arn={arn}
    />
  )
}
